import { useEffect, useState } from "react";
import { http } from "../../lib/api";
import { fValor } from "../../lib/trading";
import { TradingPanel } from "./Panel";
import { TC, TvChart } from "./TvChart";
import { M, N0, P2, agrupar, apilar, lDia, lMes, linea } from "./barras";

const PLZ = { Vista: TC.gray, "7 días": TC.blue, "14 días": TC.cyan, "28 días": TC.up, "90 días": TC.accent, "91 días": TC.orange, "180 días": TC.violet, "360 días": TC.pink };
const INS = [["Sin dato", TC.gray], ["Liquidar", TC.down], ["Reinvertir", TC.up]];
const Nota = ({ t, testid }) => <p className="mt-1 text-[10px] text-[var(--trd-accent)]" data-testid={testid}>{t}</p>;

export const useCapSeries = () => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get("/captacion/series").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  return d;
};

const SaldoCortes = ({ d }) => {
  const cs = d.cortes, u = cs[cs.length - 1];
  const series = apilar([{ id: "vista", name: "Vista", color: TC.gray, vals: cs.map((x) => x.vista), fmt: M }, { id: "plazo", name: "Plazo", color: TC.up, vals: cs.map((x) => x.plazo), fmt: M }]);
  return (
    <TradingPanel title="Saldo de captación por corte · Vista vs Plazo" ultimo={u ? fValor(u.saldo, "money", true) : "—"} cambio={`${cs.length} corte${cs.length === 1 ? "" : "s"}`} tono="flat" testid="cg-saldo"
      como="Σ saldo de capital de las cuentas con saldo en cada corte PISA importado; barra apilada: Plazo (verde) sobre Vista (gris). Un punto por corte; no se reconstruyen saldos pasados." fuente="Captación · snapshots por corte">
      <TvChart series={series} labels={cs.map((x) => lDia(x.fecha))} height={220} fmt={M} testid="cg-saldo-chart" />
      <Nota t={d.nota} testid="cg-saldo-nota" />
    </TradingPanel>
  );
};

const Movimiento = ({ d }) => {
  const cs = d.cortes, sinRet = cs.every((x) => x.movimiento.retiros == null);
  const g = agrupar(cs.map((x) => lDia(x.fecha)), [{ id: "nuevo", name: "Dinero nuevo", color: TC.up, vals: cs.map((x) => x.movimiento.nuevo), fmt: M },
    { id: "renov", name: "Renovaciones", color: TC.blue, vals: cs.map((x) => x.movimiento.renovaciones), fmt: M }, { id: "ret", name: "Retiros", color: TC.down, vals: cs.map((x) => x.movimiento.retiros), fmt: M }]);
  return (
    <TradingPanel title="Movimiento por corte · nuevo, renovaciones, retiros" testid="cg-movimiento" como={cs[cs.length - 1]?.movimiento.nota} fuente="Captación · contratos por corte">
      <TvChart series={g.series} labels={g.labels} height={200} fmt={M} testid="cg-movimiento-chart" />
      {sinRet && <Nota t="Retiros: disponible a partir del segundo corte." testid="cg-movimiento-retiros" />}
    </TradingPanel>
  );
};

const Aperturas = ({ a }) => {
  const ms = a.meses, plz = Object.keys(PLZ).filter((p) => ms.some((m) => m.por_plazo[p]));
  const series = apilar(plz.map((p) => ({ id: p, name: p, color: PLZ[p], vals: ms.map((m) => m.por_plazo[p]?.monto || 0), fmt: M })));
  return (
    <TradingPanel title="Aperturas por mes y plazo" ultimo={fValor(ms.reduce((s, m) => s + m.monto, 0), "money", true)} cambio={`${ms.reduce((s, m) => s + m.contratos, 0)} contratos`} tono="flat" testid="cg-aperturas" como={a.como} fuente="Captación · fecha de apertura">
      <TvChart series={series} labels={ms.map((m) => lMes(m.mes))} height={240} fmt={M} toggle testid="cg-aperturas-chart" />
      <Nota t={a.nota} testid="cg-aperturas-nota" />
    </TradingPanel>
  );
};

const Costo = ({ d }) => {
  const meses = d.tiie.map((t) => t.mes), porM = Object.fromEntries(d.cortes.map((x) => [x.fecha.slice(0, 7), x]));
  const v = (k) => linea(meses.map((m) => porM[m]?.[k] ?? null));
  const series = [{ id: "tiie", name: "TIIE 28", type: "line", color: TC.accent, data: linea(d.tiie.map((t) => t.valor)), fmt: P2 },
    { id: "plazo", name: "Costo plazo", type: "line", points: true, color: TC.up, data: v("costo_plazo"), fmt: P2 },
    { id: "tabla", name: "Tabla vigente (plazo)", type: "line", points: true, dashed: true, color: TC.violet, data: v("tabla_ref"), fmt: P2 },
    { id: "total", name: "Costo total (incl. vista 0%)", type: "line", points: true, color: TC.cyan, data: v("costo_total"), fmt: P2 }];
  const u = d.cortes[d.cortes.length - 1];
  return (
    <TradingPanel title="Costo de fondeo vs TIIE 28 y tabla" ultimo={u ? `${P2(u.costo_total)} · plazo ${P2(u.costo_plazo)}` : "—"} tono="flat" testid="cg-costo"
      como={`Costo = Σ(tasa × saldo) ÷ Σ saldo por corte: total (incluye vista a 0%) y solo plazo. Tabla = tasa de la tabla vigente en la apertura, ponderada por saldo. TIIE 28 al último dato del mes.${d.tabla_campana ? ` Campaña: ${Object.entries(d.tabla_campana.tasas).map(([p, t]) => `${p} ${t}%`).join(" · ")}.` : ""}`} fuente="Captación + DOF">
      <TvChart series={series} labels={meses.map(lMes)} height={220} fmt={P2} testid="cg-costo-chart" />
    </TradingPanel>
  );
};

export const Escalera = ({ e }) => {
  const [iv, setIv] = useState("S");
  const b = iv === "S" ? e.semanas : e.meses;
  const series = apilar(INS.map(([k, c]) => ({ id: k, name: k, color: c, vals: b.map((x) => x[k].saldo), fmt: M })));
  return (
    <TradingPanel title={`Escalera de vencimientos · ${iv === "S" ? "13 semanas" : "12 meses"}`} ultimo={fValor(b.reduce((s, x) => s + x.saldo, 0), "money", true)} cambio={`${b.reduce((s, x) => s + x.contratos, 0)} contratos`} tono="flat"
      iv={iv} setIv={(x) => setIv(x === "M" ? "M" : "S")} testid="cg-escalera" como={e.como} fuente={`Captación · corte ${e.corte}`}>
      <TvChart series={series} labels={b.map((x) => (iv === "S" ? lDia(x.inicio) : lMes(x.mes)))} height={220} fmt={M} testid="cg-escalera-chart" />
    </TradingPanel>
  );
};

const Concentracion = () => {
  const [c, setC] = useState(null);
  useEffect(() => { http.get("/captacion/series/concentracion").then((r) => setC(r.data)).catch(() => setC(false)); }, []);
  if (!c || !c.top.length) return null;
  const t = c.tendencia;
  return (
    <TradingPanel title="Concentración de ahorradores" ultimo={`Top-10 ${P2(t[t.length - 1].top10)}`} cambio={`Top-1 ${P2(t[t.length - 1].top1)}`} tono="flat" testid="cg-concentracion"
      como="Saldo de cada ahorrador ÷ saldo total del corte. Etiquetas anonimizadas (Ahorrador 1…10); los nombres solo aparecen en la lista de detalle. Tendencia: top-1 y top-10 por corte." fuente="Captación · snapshots por corte">
      <TvChart series={[{ id: "pct", name: "% del saldo", type: "histogram", color: TC.accent, data: c.top.map((x) => ({ value: x.pct })), fmt: P2 }]} labels={c.top.map((x) => x.etiqueta)} height={180} fmt={P2} testid="cg-conc-chart" />
      <TvChart series={[{ id: "top10", name: "Top-10", type: "line", points: true, color: TC.accent, data: t.map((x) => ({ value: x.top10 })), fmt: P2 }, { id: "top1", name: "Top-1", type: "line", points: true, color: TC.down, data: t.map((x) => ({ value: x.top1 })), fmt: P2 }]}
        labels={t.map((x) => lDia(x.fecha))} height={140} fmt={P2} testid="cg-conc-tendencia" />
      <details className="mt-2 text-[11px]" data-testid="cg-conc-detalle"><summary className="cursor-pointer text-[var(--trd-muted)] hover:text-white">Detalle (nombres)</summary>
        {c.detalle.map((x) => <p key={x.etiqueta} className="trd-mono flex justify-between gap-2 border-t border-[var(--trd-border)] py-1"><span className="truncate">{x.etiqueta} · {x.nombre}</span><span>{P2(x.pct)}</span></p>)}</details>
    </TradingPanel>
  );
};

const Barra = ({ l, v, tot, c, testid }) => (
  <div className="relative grid grid-cols-12 items-center gap-2 overflow-hidden rounded-md px-2 py-1 text-[11px]" data-testid={testid}>
    <div className="absolute inset-y-1 left-0 rounded-r opacity-40" style={{ width: `${(v / (tot || 1)) * 100}%`, background: c }} />
    <span className="relative col-span-5 truncate text-[#C9D1D9]">{l}</span><span className="trd-mono relative col-span-4 text-right">{fValor(v, "money", true)}</span><span className="trd-mono relative col-span-3 text-right text-[var(--trd-muted)]">{P2(v / (tot || 1))}</span>
  </div>
);

const Composicion = ({ u }) => (
  <TradingPanel title="Composición · PF / PM y plazo" ultimo={lDia(u.fecha)} tono="flat" testid="cg-composicion" como="Saldo del último corte por tipo de persona del ahorrador y por plazo." fuente="Captación · último corte">
    {Object.entries(u.por_tipo).map(([k, v]) => <Barra key={k} l={k} v={v} tot={u.saldo} c={k === "PM" ? TC.blue : TC.pink} testid={`cg-comp-${k}`} />)}
    <div className="my-1 border-t border-[var(--trd-border)]" />
    {Object.entries(u.por_plazo).map(([k, v]) => <Barra key={k} l={k} v={v} tot={u.saldo} c={PLZ[k] || TC.gray} testid={`cg-comp-plazo-${k.replace(/\s/g, "")}`} />)}
  </TradingPanel>
);

const Cobertura = ({ d }) => {
  const cs = d.cortes, u = cs[cs.length - 1].cobertura;
  return (
    <TradingPanel title="Sobre cobertura del Fondo de Protección" ultimo={`${u.ahorradores} ahorradores`} cambio={u.excedente != null ? `excedente ${fValor(u.excedente, "money", true)}` : null} tono="flat" testid="cg-cobertura"
      como={`Ahorradores cuyo saldo total supera ${u.udis.toLocaleString("es-MX")} UDIS × UDI ${u.udi ?? "n.d."} = ${u.limite ? fValor(u.limite, "money") : "n.d."} (validar con Cumplimiento). Barras: saldo de esos ahorradores; línea: número.`} fuente="Captación + UDI (DOF)">
      <TvChart series={[{ id: "saldo", name: "Saldo sobre cobertura", type: "histogram", color: TC.orange, data: cs.map((x) => ({ value: x.cobertura.saldo })), fmt: M },
        { id: "n", name: "Ahorradores", type: "line", points: true, scale: "left", color: TC.accent, data: cs.map((x) => ({ value: x.cobertura.ahorradores })), fmt: N0 }]} labels={cs.map((x) => lDia(x.fecha))} height={180} fmt={M} testid="cg-cobertura-chart" />
    </TradingPanel>
  );
};

const Vacio = () => <p className="trd rounded-2xl p-4 text-xs text-[var(--trd-muted)]" data-testid="cg-vacio">Aún no hay cortes PISA importados.</p>;

// Vista «Captación» en los selectores de Inicio y Analítica (mismos espacios que Crédito).
export const CapChartsResumen = () => {
  const d = useCapSeries();
  if (d === null) return <div className="trd h-[300px] animate-pulse rounded-2xl" />;
  if (!d || !d.cortes.length) return <Vacio />;
  return (
    <div className="space-y-3" data-testid="cg-resumen">
      <SaldoCortes d={d} />
      <div className="grid gap-3 lg:grid-cols-2"><Costo d={d} /><Escalera e={d.escalera} /></div>
    </div>
  );
};

// Captación → Gráficas: tablero completo.
export const CapGraficas = () => {
  const d = useCapSeries();
  if (d === null) return <div className="trd h-[300px] animate-pulse rounded-2xl" />;
  if (!d || !d.cortes.length) return <Vacio />;
  return (
    <div className="space-y-3" data-testid="cap-graficas">
      <div className="grid gap-3 lg:grid-cols-2"><SaldoCortes d={d} /><Movimiento d={d} /></div>
      <Aperturas a={d.aperturas} />
      <div className="grid gap-3 lg:grid-cols-2"><Costo d={d} /><Escalera e={d.escalera} /></div>
      <div className="grid gap-3 lg:grid-cols-2"><Concentracion /><div className="space-y-3"><Composicion u={d.cortes[d.cortes.length - 1]} /><Cobertura d={d} /></div></div>
    </div>
  );
};
